import type { ReactElement } from 'react';

import { DateText, DateTimeText } from '@/components/time/DateText';
import { cn } from '@/components/ui/cn';
import { formatInstantDate } from '@/lib/time';

import {
  actorColorIndex,
  actorInitials,
  actorLabel,
  beforeAfter,
  formatWhen,
  originLabel,
  severityTone,
} from '../event-presentation';
import type { AuditLogRow } from '../types';

/** Fecha que traía la carga y se descartó por ser posterior a ella, entre paréntesis. */
function discardedDate(row: AuditLogRow): string {
  const at = row.metadata?.discardedEventDate;
  return typeof at === 'string' ? ` (${formatInstantDate(at)})` : '';
}

/** Entrada por documento de una carga histórica: la que lleva la fecha real del evento. */
const IMPORTED_DOCUMENT = 'quality.master-list.import.document';

/** Fecha real de un evento de carga histórica (G1-2): con hora solo si la carga la
 *  traía; si el documento no traía fecha, se dice (spec §7.2). El resumen del lote y
 *  los catálogos no tienen fecha del evento propia: no muestran nada. */
function HistoricalDate({ row }: { row: AuditLogRow }): ReactElement | null {
  if (!row.eventDate) {
    if (row.action !== IMPORTED_DOCUMENT) {
      return null;
    }
    // La base exige que la fecha del evento no sea posterior a la carga: si lo era, se
    // descartó y la entrada lo anota.
    return (
      <span className="ev-date">
        {row.metadata?.eventDateAfterLoad === true
          ? `La fecha del evento${discardedDate(row)} era posterior a la carga y no se registró`
          : 'Carga histórica sin fecha del evento'}
      </span>
    );
  }
  const dayOnly = row.metadata?.eventDatePrecision === 'day';
  return (
    <span className="ev-date">
      Fecha del evento:{' '}
      {dayOnly ? (
        <DateText value={row.eventDate} kind="instant" />
      ) : (
        <DateTimeText value={row.eventDate} />
      )}
    </span>
  );
}

export const COLUMNS = [
  'Fecha y hora',
  'Actor',
  'Acción',
  'Entidad',
  'Antes → Después',
  'Origen',
] as const;

const TONE_CLASS: Record<string, string> = {
  info: 'b-info',
  warning: 'b-warn',
  critical: 'b-red',
};

function DiffCell({ row }: { row: AuditLogRow }): ReactElement {
  const { before, after } = beforeAfter(row);
  if (before === null && after === null) {
    return <span className="dim-mono">—</span>;
  }
  return (
    <span className="diff">
      {before !== null && <span className="was">{before}</span>}
      {before !== null && after !== null && ' → '}
      {after !== null && <span className="now">{after}</span>}
    </span>
  );
}

/** Bitácora de auditoría en tabla. Append-only, orden cronológico inverso (lo da
 *  el backend). Cero enums crudos: acción y severidad se muestran legibles. */
export function EventTable({ rows }: { rows: AuditLogRow[] }): ReactElement {
  return (
    <table className="t">
      <caption className="sr-only">
        Bitácora de auditoría de la empresa activa, del evento más reciente al más antiguo
      </caption>
      <thead>
        <tr>
          {COLUMNS.map((c) => (
            <th key={c} scope="col">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const when = formatWhen(row.occurredAt);
          const color = actorColorIndex(row);
          return (
            <tr key={row.id} className={cn(severityTone(row.severity) === 'critical' && 'alert')}>
              <td className="dim-mono">
                {when.date}
                {when.time && (
                  <>
                    <br />
                    {when.time}
                  </>
                )}
                {row.loadedHistorically === true && <HistoricalDate row={row} />}
              </td>
              <td>
                <div className="who">
                  <span
                    className={cn('av sm', color === 0 ? 'sys' : `p${color}`)}
                    aria-hidden="true"
                  >
                    {actorInitials(row)}
                  </span>
                  <span className="nm">{actorLabel(row)}</span>
                </div>
              </td>
              <td>
                <span className={cn('badge', TONE_CLASS[severityTone(row.severity)])}>
                  <span className="dotb" />
                  {row.actionLabel}
                </span>
              </td>
              <td>
                {row.targetCode ? (
                  <span className="code">{row.targetCode}</span>
                ) : (
                  <span className="dim-mono">{row.targetModule ?? '—'}</span>
                )}
              </td>
              <td>
                <DiffCell row={row} />
              </td>
              <td className="dim-mono">{originLabel(row)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
